/* eslint-disable no-use-before-define */
import { schemas } from '#oaf_schema.registry';
import { createStore, createWireStore } from '@oneappexchange/lwc-wired';
import equalityFn from 'zustand/shallow';
import { IS_DEVELOPMENT, PUBLIC_URL } from '~/config';
import { getListing, upsertListing } from '~/modules/services/listingService';
import { PaymentTypes } from '~/modules/services/pricing/models/Pricing';
import { upsertPublisher } from '~/modules/services/publisherService';
import { initialState as initialJp } from '~/modules/stores/publisher/japanesePublisherEditorStore';
import { generateKeyForIterable, getUniqueIteratorKey, scrollToTop } from '~/modules/utilities/browserUtilities';
import { getBusinessApprovalStatus } from '~/modules/utilities/businessApprovalsUtilities';
import { getDateFromDatetime } from '~/modules/utilities/dateUtilities';
import { failedToSavePublisherError, handleException, invalidMarketplaceError, throwDevelopmentOrLog, unknownListingTypeError, unresponsiveServerError } from '~/modules/utilities/errorUtilities';
import { FailedSolutionIdExchange } from '~/modules/utilities/exceptions/FailedSolutionIdExchange';
import { ListingEditorStatus, getListingEditorStatus } from '~/modules/utilities/listingEditorStatus';
import { cleanSupportInfoData, moveItem, obtainSolutionAndTrialTemplateIds, removeEmptyContentItems } from '~/modules/utilities/listingUtilities';
import { ListingValidator } from '~/modules/utilities/listingValidator';
import { logger } from '~/modules/utilities/logger';
import { notifyFailure, notifySuccess } from '~/modules/utilities/notificationUtilities';
import { isObject } from '~/modules/utilities/objectUtilities';
import { handlePricingError, hasChangedToCheckout, pricingModelToRecord, validatePricingClientSide } from '~/modules/utilities/pricingUtilities';
import { upsertQueryParams } from '~/modules/utilities/routingUtilities';
import { SOLUTION_TYPES_SR_REQUIRED, TECHNOLOGY_EXTENSION_MAP, isTableauType } from '~/modules/utilities/schemaUtilities';
import { getStepLabel } from '~/modules/utilities/stepListUtilities';
import { BUSINESS_APPROVAL_STATUSES, approvalsStore } from '../approvalsStore';
import { i18nStore } from '../i18nStore';
import { pricingStore } from '../pricingStore';
import { japanesePublisherEditorStore } from '../publisher/japanesePublisherEditorStore';
import { publisherEditorStore } from '../publisher/publisherEditorStore';
import { publisherStore, selectISVClickthrough, selectLogoSetItems as selectLogoSetItemsPublisher, selectPublisherCountryTaxes, selectPublisherId } from '../publisher/publisherStore';
import { LISTINGS_ROUTE, LISTING_EDITOR_ROUTE, PUBLISHER_ROUTE, navigate, routerStore, selectMatch } from '../routerStore';
import { consultantListingStore } from './consultantListingStore';
import { forceListingStore, selectCreditorIdentifier, selectPackageVersionId, selectSolutionId, selectTestDrive, selectTrialTemplateId } from './forceListingStore';
import { hasPackageVersionSecurityReviewed, isSolutionSecurityReviewed, listingSolutionsStore } from './listingSolutionsStore';
const getDefaultContentItem = () => schemas['listing/plugins/Content/ContentItem'].new({
  data: schemas['listing/plugins/Content/Items/ContentFile'].new({
    type: ''
  }),
  type: '',
  iteratorKey: getUniqueIteratorKey()
});
const CONTENT_TYPE_TO_SCHEMA_ID_MAP = {
  Url: 'listing/plugins/Content/Items/ContentUrl',
  File: 'listing/plugins/Content/Items/ContentFile'
};
const VALID_LEADS_KEYS = ['watchADemo', 'testDrive', 'createTrial', 'installApp', 'watchLearnMore'];
let activeTechnologyStore;
const initialState = {
  listing: schemas['listing/BaseListing'].new(),
  uploadingAssets: 0,
  hasChanged: false,
  isLoading: false,
  isSaving: false,
  needToSavePublisher: false
};
export const listingStore = createStore(initialState, (set, get) => ({
  reset() {
    set(function reset() {
      return initialState;
    });
  },
  change() {
    set(function change(draft) {
      draft.hasChanged = true;
    });
  },
  changeNeedsToSavePublisher() {
    set(function changeNeedsToSavePublisher(draft) {
      draft.needToSavePublisher = true;
      draft.hasChanged = true;
    });
  },
  /**
   * You need to pass a type if id is undefined, otherwise the type is inferred from the listing
   */
  async initialize({
    id,
    newListingType = 'force/package',
    marketplace = 'English'
  }) {
    var _listing$technology, _listing$extensions$f, _technologyStoreIniti;
    let listing = get().listing;
    let hasChanged = get().hasChanged;
    let solution;
    let trialTemplate;
    const editorStatus = getListingEditorStatus(id, newListingType, listing);
    switch (editorStatus) {
      case ListingEditorStatus.DifferentListing:
        {
          set(function loading(draft) {
            draft.isLoading = true;
          });
          const payload = await getListing(id);
          listing = schemas['listing/BaseListing'].new(payload === null || payload === void 0 ? void 0 : payload.listing);
          solution = payload === null || payload === void 0 ? void 0 : payload.solution;
          trialTemplate = payload === null || payload === void 0 ? void 0 : payload.trialTemplate;
          hasChanged = false;
          break;
        }
      case ListingEditorStatus.NewListing:
        {
          set(function loading(draft) {
            draft.isLoading = true;
          });
          listing = schemas['listing/BaseListing'].new({
            publisher: publisherStore.getState().publisher.id,
            org62AccountId: publisherStore.getState().publisher.org62AccountId,
            technology: newListingType
          });
          hasChanged = false;
          break;
        }
      default:
        // If we're not dealing with a new or different listing, we don't need to re-initialize the store
        return;
    }
    set(function initializing(draft) {
      draft.listing = listing;
      draft.hasChanged = hasChanged;
      // Initialize required plugins
      selectSupportInfo(selectListing(draft), true);
      // Initialize ContentItems and sort them if needed
      const contentItems = selectContentItems(draft);
      if (contentItems.length === 0) {
        contentItems.push(getDefaultContentItem());
      } else {
        sortItems(contentItems);
      }
      //Sort Carousel items if there are any
      const carouselItems = selectCarouselItems(draft);
      if (carouselItems.length > 0) {
        sortItems(carouselItems);
      }
    });

    // If we're attempting to create a JP listing & the publisher is missing JP localization data,
    // create the JP localization data by copying the 'name' and 'logo' fields from the EN data.
    if (marketplace === 'AppExchange_Japanese') {
      var _publisher$marketplac, _publisher$marketplac2;
      const {
        publisher
      } = publisherStore.getState();

      // Upsert JP publisher
      const jpPublisher = (_publisher$marketplac = (_publisher$marketplac2 = publisher.marketplaceLocalization) === null || _publisher$marketplac2 === void 0 ? void 0 : _publisher$marketplac2.jp) !== null && _publisher$marketplac !== void 0 ? _publisher$marketplac : initialJp.publisher;
      // Sync stores to upserted publisher
      publisherEditorStore.setPublisher(publisher);
      japanesePublisherEditorStore.setPublisher(jpPublisher);
      // Reset the changed state so we can use this boolean when determining if we modified the publisher and need to save it
      publisherEditorStore.resetChanged();

      // Ensure publisher is Active
      if (jpPublisher.status !== 'Active') {
        japanesePublisherEditorStore.setActive();
      }
      // Ensure publisher has a name
      if (!jpPublisher.name) {
        japanesePublisherEditorStore.setName(publisher.name);
      }
      // Ensure publisher has a logo
      const logo = selectLogoSetItemsPublisher(japanesePublisherEditorStore.getState()).find(test => test.logoType === 'Logo');
      if (!(logo !== null && logo !== void 0 && logo.mediaId)) {
        var _enLogo$mediaId;
        const enLogo = selectLogoSetItemsPublisher(publisherEditorStore.getState()).find(test => test.logoType === 'Logo');
        japanesePublisherEditorStore.setSmallLogoUrl((_enLogo$mediaId = enLogo === null || enLogo === void 0 ? void 0 : enLogo.mediaId) !== null && _enLogo$mediaId !== void 0 ? _enLogo$mediaId : '');
      }

      // If any changes occurred, save the publisher
      if (publisherEditorStore.getState().hasChanged) {
        const publisherSaved = await publisherEditorStore.savePublisher(false, {
          failedLabel: i18nStore.getState().i18n.notifications.japaneseCompanyInfoMissing({
            URL: `${PUBLIC_URL}/${PUBLISHER_ROUTE}?showInputErrors=true`
          })
        });
        if (!publisherSaved) {
          failedToSavePublisherError(publisher);
          return;
        }
      }
    }
    const technologyType = (_listing$technology = listing.technology) !== null && _listing$technology !== void 0 ? _listing$technology : newListingType;
    const extensionType = TECHNOLOGY_EXTENSION_MAP[technologyType];
    let technologyStore;
    let technologyStoreName;
    let technologyStoreInitializePayload;
    switch (technologyType) {
      case 'force/copilot':
      case 'force/bolt':
      case 'force/flow':
      case 'force/lightning':
      case 'force/package':
      case 'salesforce-platform/api':
      case 'b2c-commerce/package':
      case 'tableau/accelerator':
        technologyStoreName = 'PACKAGE_LISTING';
        technologyStore = forceListingStore;
        technologyStoreInitializePayload = () => ({
          solution,
          trialTemplate,
          marketplace
        });
        break;
      // case 'quip/package':
      case 'b2c-commerce/api':
      case 'marketing-cloud/api':
        technologyStoreName = 'API_LISTING';
        technologyStore = forceListingStore;
        technologyStoreInitializePayload = () => ({
          solution,
          trialTemplate,
          marketplace
        });
        break;
      case 'consultant':
        {
          technologyStoreName = 'CONSULTANT_LISTING';
          technologyStore = consultantListingStore;
          technologyStoreInitializePayload = () => ({
            marketplace
          });
          break;
        }
      default:
        {
          unknownListingTypeError(`Unknown listing techology type: ${technologyType}`);
          return;
        }
    }

    // Unsubscribe previously active technology store if it doesn't match our listing extension
    if (activeTechnologyStore && activeTechnologyStore.type !== extensionType) {
      activeTechnologyStore.unsubscribe();
      activeTechnologyStore = undefined;
    }

    // This check ensures we only subscribe to the same store once (should never happen, but doesn't hurt to be safe)
    if (!activeTechnologyStore) {
      // Any time our extension store updates, update our internal extension
      activeTechnologyStore = {
        type: extensionType,
        store: technologyStore,
        unsubscribe: technologyStore.subscribe(data => {
          const fn = draft => {
            const index = draft.listing.extensions.findIndex(test => test.extensionType === extensionType);
            if (index !== -1) {
              draft.listing.extensions[index].data = data;
            } else {
              draft.listing.extensions.push({
                extensionType,
                data
              });
            }
          };
          if (IS_DEVELOPMENT) {
            // Give the anonymous function 'fn' the name of the technology store to make debugging with redux dev tools easier
            // https://stackoverflow.com/a/44288357/15101549
            Object.defineProperty(fn, 'name', {
              writable: true,
              value: technologyStoreName || 'MISSING_TECHNOLOGY_STORE_NAME'
            });
          }
          set(fn);
        },
        // Select the data field of a technology store and pass it to the function above
        ({
          data
        }) => data)
      };
    }
    technologyStore.initialize(get, editorStatus, (_listing$extensions$f = listing.extensions.find(test => test.extensionType === extensionType)) === null || _listing$extensions$f === void 0 ? void 0 : _listing$extensions$f.data, (_technologyStoreIniti = technologyStoreInitializePayload) === null || _technologyStoreIniti === void 0 ? void 0 : _technologyStoreIniti());
    set(function initialized(draft) {
      draft.isLoading = false;
    });
  },
  upsertPlugin(plugin, draft) {
    if (draft) {
      const index = draft.plugins.findIndex(({
        pluginType
      }) => pluginType === plugin.pluginType);
      if (index === -1) {
        draft.plugins.push(plugin);
      } else {
        draft.plugins[index].data = plugin.data;
      }
    }
    return plugin;
  },
  setId(id) {
    set(function setId(draft) {
      draft.listing.id = id;
    });
  },
  setName(name) {
    set(function setName(draft) {
      draft.listing.name = name;
    });
  },
  setSEOTitle(seoTitle) {
    set(function setSEOTitle(draft) {
      draft.listing.seoTitle = seoTitle;
    });
  },
  setBriefDescription(description) {
    set(function setBriefDescription(draft) {
      draft.listing.description = description;
    });
  },
  setFullDescription(fullDescription) {
    set(function setFullDescription(draft) {
      draft.listing.fullDescription = fullDescription;
    });
  },
  setBusinessEmail(email) {
    set(function setBusinessEmail(draft) {
      selectSupportInfo(draft.listing, true).businessEmail = email;
    });
  },
  setReviewsEmail(email) {
    set(function setReviewsEmail(draft) {
      selectSupportInfo(draft.listing, true).reviewsEmail = email;
    });
  },
  setSupportEmailAddress(email) {
    set(function setSupportEmailAddress(draft) {
      selectSupportInfo(draft.listing, true).supportEmailAddress = email;
    });
  },
  setSupportPhoneNumber(number) {
    set(function setSupportPhoneNumber(draft) {
      selectSupportInfo(draft.listing, true).supportPhoneNumber = number;
    });
  },
  setSupportDetails(text) {
    set(function setSupportDetails(draft) {
      selectSupportInfo(draft.listing, true).supportDetails = text;
    });
  },
  setEnableMarketingUse(enableMarketingUse) {
    set(function setEnableMarketingUse(draft) {
      draft.listing.enableMarketingUse = enableMarketingUse;
    });
  },
  setSmallLogoUrl(mediaId) {
    set(function setSmallLogoUrl(draft) {
      const items = selectLogoSetItems(draft.listing);
      const logoIndex = items.findIndex(test => test.logoType === 'Logo');
      const logo = items[logoIndex];
      if (!mediaId && logo) {
        items.splice(logoIndex, 1);
      } else {
        if (!logo) {
          items.push(schemas['listing/plugins/LogoSet/LogoSetItem'].new({
            logoType: 'Logo',
            mediaId
          }));
        } else {
          logo.mediaId = mediaId;
        }
      }
    });
  },
  setLargeLogoUrl(mediaId) {
    set(function setLargeLogoUrl(draft) {
      const items = selectLogoSetItems(draft.listing);
      const logoIndex = items.findIndex(test => test.logoType === 'Big Logo');
      const logo = items[logoIndex];
      if (!mediaId && logo) {
        items.splice(logoIndex, 1);
      } else {
        if (!logo) {
          items.push(schemas['listing/plugins/LogoSet/LogoSetItem'].new({
            logoType: 'Big Logo',
            mediaId
          }));
        } else {
          logo.mediaId = mediaId;
        }
      }
    });
  },
  incrementUploadingAssets() {
    set(function incrementUploadingAssets(draft) {
      draft.uploadingAssets++;
    });
  },
  decrementUploadingAssets() {
    set(function decrementUploadingAssets(draft) {
      draft.uploadingAssets--;
      if (draft.uploadingAssets < 0) {
        draft.uploadingAssets = 0;
      }
    });
  },
  addCarouselItem(item) {
    set(function addCarouselItem(draft) {
      const carouselItems = selectCarouselItems(draft);
      item.data.position = carouselItems.length;
      carouselItems.push(item);
    });
  },
  editCarouselItem(item, index) {
    set(function editCarouselItem(draft) {
      const existingItem = selectCarouselItems(draft)[index];
      if (!existingItem) {
        throwDevelopmentOrLog(new Error(`Failed to find carousel item at index '${index}'`));
        return;
      }
      existingItem.type = item.type;
      const itemData = item.data;
      if (itemData) {
        for (const key of Object.keys(itemData)) {
          existingItem.data[key] = itemData[key];
        }
      }
    });
  },
  reOrderCarrouselItem(indexToChange, indexTarget) {
    set(function reOrderCarrouselItem(draft) {
      const carouselItems = selectCarouselItems(draft);
      moveItem(indexTarget, indexToChange, carouselItems);
      //Recalculate position
      carouselItems.forEach((item, index) => {
        item.data.position = index;
      });
    });
  },
  removeCarouselItem(index) {
    set(function removeCarouselItem(draft) {
      //We need to re-calculate the position of the carousel items
      selectCarouselItems(draft).splice(index, 1);
      const carouselItems = selectCarouselItems(draft);
      for (let i = 0; i < carouselItems.length; i++) {
        carouselItems[i].data.position = i;
      }
    });
  },
  addContentItem(item = getDefaultContentItem()) {
    set(function addCarouselItem(draft) {
      const contentItems = selectContentItems(draft);
      item.data.position = contentItems.length;
      contentItems.push(item);
    });
  },
  editContentItem(item, index) {
    set(function editContentItem(draft) {
      const existingItem = selectContentItems(draft)[index];
      if (!existingItem) {
        throwDevelopmentOrLog(new Error(`Failed to find content item at index '${index}'`));
        return;
      }

      // When content type changes we need to recreate the data payload to match the correct schema
      if (item.type && existingItem.type !== item.type) {
        var _existingItem$data$ty, _existingItem$data;
        const schemaId = CONTENT_TYPE_TO_SCHEMA_ID_MAP[item.type];
        const schema = schemas[schemaId];
        if (!schema) {
          throwDevelopmentOrLog(new Error(`Failed to find schema type for content of type '${item.type}'`));
          return;
        }

        // We can preserve the title since both ContentFile & ContentUrl schemas have this field (and it has the same meaning)
        existingItem.data = schemas[schemaId].new({
          title: existingItem.data.title || '',
          type: (_existingItem$data$ty = (_existingItem$data = existingItem.data) === null || _existingItem$data === void 0 ? void 0 : _existingItem$data.type) !== null && _existingItem$data$ty !== void 0 ? _existingItem$data$ty : ''
        });
      }
      existingItem.type = item.type;
      const itemData = item.data;
      if (itemData) {
        for (const key of Object.keys(itemData)) {
          existingItem.data[key] = itemData[key];
        }
      }
    });
  },
  removeContentItem(index) {
    set(function removeContentItem(draft) {
      //We need to re-calculate the position of the content items
      selectContentItems(draft).splice(index, 1);
      const contentItems = selectContentItems(draft);
      for (let i = 0; i < contentItems.length; i++) {
        contentItems[i].data.position = i;
      }
    });
  },
  removeEmptyContent() {
    set(function removeEmptyContent(draft) {
      removeEmptyContentItems(selectContentItems(draft));
    });
  },
  removeEmptySupportInfo() {
    set(function removeEmptySupportInfo(draft) {
      const pluginSupportInformation = 'listing/plugins/SupportInformation';
      const plugin = draft.listing.plugins.find(e => e.pluginType === pluginSupportInformation);
      if (plugin) {
        plugin.data = cleanSupportInfoData(selectSupportInfo(selectListing(draft), true));
      }
    });
  },
  reOrderContentItem(indexToChange, indexTarget) {
    set(function reOrderContentItem(draft) {
      const contentItems = selectContentItems(draft);
      moveItem(indexTarget, indexToChange, contentItems);
      //Recalculate position
      contentItems.forEach((item, index) => {
        item.data.position = index;
      });
    });
  },
  editDemoVideo(url) {
    set(function editDemoVideo(draft) {
      const demo = schemas['listing/plugins/Demo/DemoVideo'].new({
        url: url !== '' ? url : undefined,
        videoType: 'YouTube'
      });
      const demoItems = selectDemo(draft.listing, true);
      if (demoItems.items.length === 0) {
        demoItems.items.push(demo);
      } else {
        demoItems.items[0] = demo;
      }
    });
  },
  removeDemoVideo() {
    set(function removeDemoVideo(draft) {
      const index = draft.listing.plugins.findIndex(({
        pluginType
      }) => pluginType === 'listing/plugins/Demo');
      draft.listing.plugins.splice(index, 1);
    });
  },
  setLeadsOrg(leadsOrg) {
    set(function setLeadsOrg(draft) {
      selectLeadTrialInformation(draft).leadsOrgID = leadsOrg;
    });
  },
  uncheckLeads() {
    set(function uncheckLeads(draft) {
      selectLeadTrialInformation(draft).enableDemoLeads = false;
      selectLeadTrialInformation(draft).enableTestDriveLeads = false;
      selectLeadTrialInformation(draft).enableTrialLeads = false;
      selectLeadTrialInformation(draft).enableInstallLeads = false;
      selectLeadTrialInformation(draft).enableLearnMoreLeads = false;
    });
  },
  setLeadIsChecked(isChecked, typeOfLead) {
    set(function setLeadIsChecked(draft) {
      if (!VALID_LEADS_KEYS.includes(typeOfLead)) {
        logger.error(`LISTING_STORE: Unknown typeOfLead '${typeOfLead}' sent to setLeadIsChecked`);
        return;
      }
      switch (typeOfLead) {
        case 'watchADemo':
          {
            selectLeadTrialInformation(draft).enableDemoLeads = isChecked;
            break;
          }
        case 'testDrive':
          {
            selectLeadTrialInformation(draft).enableTestDriveLeads = isChecked;
            break;
          }
        case 'createTrial':
          {
            selectLeadTrialInformation(draft).enableTrialLeads = isChecked;
            break;
          }
        case 'installApp':
          {
            selectLeadTrialInformation(draft).enableInstallLeads = isChecked;
            break;
          }
        case 'watchLearnMore':
          {
            selectLeadTrialInformation(draft).enableLearnMoreLeads = isChecked;
            break;
          }
        default:
          {
            throw new Error(`Unknown lead type: ${typeOfLead}`);
          }
      }
    });
  },
  setLeadCampaignID(campaignID, typeOfLead) {
    set(function setLeadCampaignID(draft) {
      if (!VALID_LEADS_KEYS.includes(typeOfLead)) {
        logger.error(`LISTING_STORE: Unknown typeOfLead '${typeOfLead}' sent to setLeadCampaignID`);
        return;
      }
      switch (typeOfLead) {
        case 'watchADemo':
          {
            selectLeadTrialInformation(draft).demoCampaignID = campaignID;
            break;
          }
        case 'testDrive':
          {
            selectLeadTrialInformation(draft).testDriveCampaignID = campaignID;
            break;
          }
        case 'createTrial':
          {
            selectLeadTrialInformation(draft).trialCampaignID = campaignID;
            break;
          }
        case 'installApp':
          {
            selectLeadTrialInformation(draft).installCampaignID = campaignID;
            break;
          }
        case 'watchLearnMore':
          {
            selectLeadTrialInformation(draft).learnMoreCampaignID = campaignID;
            break;
          }
        default:
          {
            throw new Error(`Unknown lead type: ${typeOfLead}`);
          }
      }
    });
  },
  removeTrialTemplate() {
    set(function removeTrialTemplate(draft) {
      var _draft$listing$plugin, _draft$listing$plugin2;
      const index = (_draft$listing$plugin = (_draft$listing$plugin2 = draft.listing.plugins) === null || _draft$listing$plugin2 === void 0 ? void 0 : _draft$listing$plugin2.findIndex(({
        pluginType
      }) => pluginType === 'listing/plugins/TrialTemplate')) !== null && _draft$listing$plugin !== void 0 ? _draft$listing$plugin : -1;
      if (index !== -1) {
        var _draft$listing$plugin3;
        (_draft$listing$plugin3 = draft.listing.plugins) === null || _draft$listing$plugin3 === void 0 ? void 0 : _draft$listing$plugin3.splice(index, 1);
      }
    });
  },
  async dispatchChangeOnCheckoutListing() {
    set(function setPrivate(draft) {
      draft.listing.public = false;
    });
    const result = await listingStore.saveListing({
      redirectAfterSave: false,
      isSubmitForApproval: true
    });
    return result;
  },
  async saveListing({
    redirectAfterSave = true,
    syncPricing = false,
    isSubmitForApproval = false
  } = {}) {
    set(function beginSave(draft) {
      draft.isSaving = true;
    });

    //Remove contentItems that are not completed
    listingStore.removeEmptyContent();
    // //Remove empty fields on SupportInformation plugin
    listingStore.removeEmptySupportInfo();
    //If enableTestDrive is off, then delete the test drive data
    forceListingStore.deleteTestDriveData();
    let succeeded = false;
    let obtainedListingResult;
    try {
      obtainedListingResult = await obtainSolutionAndTrialTemplateIds();
      const listing = obtainedListingResult.listing;
      const forceListing = selectExtensionListing(listing);
      if (!forceListing.checkoutEnabledDate && hasChangedToCheckout()) {
        forceListing.checkoutEnabledDate = getDateFromDatetime(new Date().toISOString());
      }
      const response = await upsertListing(listing).then(async result => {
        var _obtainedListingResul;
        listingStore.setId(result.id);
        (_obtainedListingResul = obtainedListingResult) === null || _obtainedListingResul === void 0 ? void 0 : _obtainedListingResul.setListingId(result.id);
        if (!result.external_id) {
          throw new Error('Attempted to save pricing information without external id of listing');
        }
        if (get().listing.technology !== 'consultant') {
          var _result$id, _result$external_id;
          await pricingStore.save((_result$id = result.id) !== null && _result$id !== void 0 ? _result$id : '', (_result$external_id = result.external_id) !== null && _result$external_id !== void 0 ? _result$external_id : '', listing.org62AccountId, !listing.public, syncPricing, isSubmitForApproval);
        }
        // Update Publisher if Country or PublisherTaxes change
        if (get().needToSavePublisher) {
          const publisherSaved = await upsertPublisher(publisherStore.getState().publisher);
          if (!publisherSaved) {
            notifyFailure(i18nStore.getState().i18n.notifications.errorSavingPublisherTaxes({
              TEXT: i18nStore.getState().i18nStaticCache.globalHeader.viewCompanyInfo,
              URL: PUBLISHER_ROUTE
            }));
            throw new Error('Failed to save publisher');
          }
          // Refresh publisher in order to get the publisher Taxes with their Ids
          await publisherStore.fetchPublisher();
        }
        return result;
      });
      pricingStore.resetRequireResubmission();
      logger.debug(`Saved ${response.type}: ${response.id}`);
      notifySuccess(i18nStore.getState().i18nStaticCache.notifications.listingSaved);
      if (selectExtensionListing(listing).marketplace === 'AppExchange_Japanese') {
        publisherStore.setHasJapaneseListings(true);
      }
      succeeded = true;
    } catch (exception) {
      var _obtainedListingResul2;
      // Before we persist changes to the listing, we need to replace solution and trial template IDs with OAF IDs.
      // This modification to the listing needs to be undone if the fail saves, the resetState call here accomplishes that.
      (_obtainedListingResul2 = obtainedListingResult) === null || _obtainedListingResul2 === void 0 ? void 0 : _obtainedListingResul2.resetState();
      if (exception instanceof FailedSolutionIdExchange || !isObject(exception)) {
        handleException(exception, 'listingStore.saveListing');
        notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
      } else if (exception.response) {
        var _axiosError$config$ur;
        const axiosError = exception;
        if ((_axiosError$config$ur = axiosError.config.url) !== null && _axiosError$config$ur !== void 0 && _axiosError$config$ur.includes('pricing_plan')) {
          handlePricingError(axiosError);
        } else if (axiosError.response.status === 400 && Array.isArray(axiosError.response.data.error)) {
          // This appears to be a OAF validation issue
          const errors = axiosError.response.data.error;
          logger.warn(`Failed to save OAF: ${JSON.stringify(axiosError.response.data)}`);
          const stepsWithErrors = listingStoreValidator.getStepsWithErrors(errors);
          const foundErrorInStep = stepsWithErrors.values().next().value;
          if (foundErrorInStep) {
            notifyFailure(i18nStore.getState().i18n.notifications.listingSaveFailedSaveStep({
              STEP: getStepLabel(foundErrorInStep, get().listing.technology, i18nStore.getState().i18nStaticCache.listingStep),
              URL: `${LISTING_EDITOR_ROUTE}?${upsertQueryParams(['step', foundErrorInStep], ['showInputErrors', '1'])}`
            }));
          } else {
            logger.error(`Encountered a validation error, but failed to find the correct step it belongs to: ${JSON.stringify(errors)}`);
            notifyFailure(i18nStore.getState().i18nStaticCache.notifications.listingSaveFailed);
          }
        } else {
          logger.error(`Encountered an unknown server error: ${JSON.stringify(axiosError)}`);
          notifyFailure(i18nStore.getState().i18nStaticCache.notifications.genericError);
        }
      } else if (exception.request) {
        unresponsiveServerError('listingStore.saveListing');
      } else {
        handleException(exception, 'listingStore.saveListing');
      }
    }
    set(function endSave(draft) {
      draft.isSaving = false;
      if (succeeded) {
        draft.hasChanged = false;
        draft.needToSavePublisher = false;
      }
    });
    if (succeeded && redirectAfterSave) {
      navigate(LISTINGS_ROUTE);
    }
    return succeeded;
  },
  setPublic(status) {
    set(function setPublic(draft) {
      draft.listing.public = status;
    });
  },
  setTrialTemplate(trialTemplate) {
    set(function setTrialTemplate(draft) {
      // solutionId gets set when saving a listing (we exchange selectedTrialTemplateId for it)
      selectTrialTemplate(draft, true).solutionId = '';
      forceListingStore.setSelectedTrialTemplateId(trialTemplate.id);
    });
  },
  setTrialTemplateSolutionId(solutionId) {
    set(function setTrialTemplateSolutionId(draft) {
      selectTrialTemplate(draft, true).solutionId = solutionId;
    });
  },
  setBusinessNeeds(categoryName, isCategorySelected) {
    set(function setBusinessNeeds(draft) {
      var _draft$listing;
      if (!draft.listing.businessNeeds) {
        draft.listing.businessNeeds = {};
      }
      const categoryToEdit = (_draft$listing = draft.listing) === null || _draft$listing === void 0 ? void 0 : _draft$listing.businessNeeds[categoryName];
      if (!categoryToEdit) {
        draft.listing.businessNeeds[categoryName] = {
          categories: [],
          isSelected: isCategorySelected
        };
      } else {
        if (categoryToEdit.categories.length === 0 && !isCategorySelected) {
          var _draft$listing2;
          (_draft$listing2 = draft.listing) === null || _draft$listing2 === void 0 ? true : delete _draft$listing2.businessNeeds[categoryName];
        } else {
          categoryToEdit.isSelected = isCategorySelected;
        }
      }
    });
  },
  setBusinessNeedsCategories(categoryName, subCategory, isSubCategorySelected) {
    set(function setBusinessNeedsCategories(draft) {
      var _draft$listing3;
      if (!draft.listing.businessNeeds) {
        draft.listing.businessNeeds = {};
      }
      const categoryToEdit = (_draft$listing3 = draft.listing) === null || _draft$listing3 === void 0 ? void 0 : _draft$listing3.businessNeeds[categoryName];
      if (!categoryToEdit) {
        draft.listing.businessNeeds[categoryName] = {
          categories: [subCategory],
          isSelected: false
        };
      } else {
        if (isSubCategorySelected) {
          categoryToEdit.categories.push(subCategory);
        } else {
          if (categoryToEdit.categories.length === 1 && !categoryToEdit.isSelected) {
            var _draft$listing4;
            (_draft$listing4 = draft.listing) === null || _draft$listing4 === void 0 ? true : delete _draft$listing4.businessNeeds[categoryName];
          } else {
            const subCategoryIndex = categoryToEdit.categories.findIndex(element => element === subCategory);
            categoryToEdit.categories.splice(subCategoryIndex, 1);
          }
        }
      }
    });
  }
}), 'LISTING', IS_DEVELOPMENT);
export const WireListingStore = createWireStore(listingStore);

// Initialize the store only when we have a publisher and we're in the right page
export async function checkShouldInitialize() {
  const {
    publisher
  } = publisherStore.getState();
  const {
    url,
    queryString
  } = routerStore.getState();
  if (!publisher || !url || !LISTING_EDITOR_ROUTE.includes(url)) {
    var _activeTechnologyStor, _activeTechnologyStor2;
    (_activeTechnologyStor = activeTechnologyStore) === null || _activeTechnologyStor === void 0 ? void 0 : _activeTechnologyStor.unsubscribe();
    (_activeTechnologyStor2 = activeTechnologyStore) === null || _activeTechnologyStor2 === void 0 ? void 0 : _activeTechnologyStor2.store.reset();
    activeTechnologyStore = undefined;
    listingStore.reset();
    pricingStore.reset();
    return;
  }
  let id;
  let marketplace;
  let newListingType = 'force/package';
  if (queryString) {
    var _params$get, _params$get2, _ref;
    const params = new URLSearchParams(queryString);
    id = (_params$get = params.get('id')) !== null && _params$get !== void 0 ? _params$get : undefined;
    newListingType = (_params$get2 = params.get('type')) !== null && _params$get2 !== void 0 ? _params$get2 : newListingType;
    marketplace = (_ref = params.get('marketplace')) !== null && _ref !== void 0 ? _ref : undefined;
  }

  // Validate marketplace param
  if (marketplace && !schemas['listing/extensions/force/listings/Listing'].get().properties.marketplace.enum.includes(marketplace)) {
    invalidMarketplaceError(marketplace);
    return;
  }
  const currentListing = listingStore.getState().listing;
  await listingStore.initialize({
    id,
    newListingType,
    marketplace
  });
  const listingApprovalStatus = getBusinessApprovalStatus(forceListingStore.getState().data.businessApproval);
  pricingStore.initialize({
    id,
    newListingType,
    currentListing,
    listingApprovalStatus
  });
  scrollToTop();
}
routerStore.subscribe(checkShouldInitialize, selectMatch, equalityFn);
publisherStore.subscribe(checkShouldInitialize, selectPublisherId);
function checkShouldChange() {
  const {
    url
  } = routerStore.getState();
  const {
    publisher
  } = publisherStore.getState();
  if (!publisher || !url || !LISTING_EDITOR_ROUTE.includes(url)) {
    return;
  }
  if (!listingStore.getState().hasChanged && !listingStore.getState().isLoading) {
    listingStore.change();
  }
}
listingStore.subscribe(checkShouldChange, selectListing);
function checkNeedsToSavePublisher() {
  const {
    url
  } = routerStore.getState();
  if (!url || !LISTING_EDITOR_ROUTE.includes(url)) {
    return;
  }
  if (!publisherStore.getState().isLoading) {
    listingStore.changeNeedsToSavePublisher();
  }
}
publisherStore.subscribe(checkNeedsToSavePublisher, selectPublisherCountryTaxes, equalityFn);

// Components connected to the listingStore should use this globally available instance of the ListingValidator.
export const listingStoreValidator = new ListingValidator(initialState.listing, {
  isSolutionValid: () => {
    if (SOLUTION_TYPES_SR_REQUIRED.includes(selectListingType(listingStore.getState()))) {
      return Boolean(isSolutionSecurityReviewed(listingSolutionsStore.getState(), selectSolutionId(forceListingStore.getState())));
    } else if (listingStore.getState().listing.technology.startsWith('b2c-commerce') || isTableauType(listingStore.getState().listing.technology)) {
      return Boolean(selectSolutionId(forceListingStore.getState()));
    }
    return hasPackageVersionSecurityReviewed(listingSolutionsStore.getState(), selectPackageVersionId(forceListingStore.getState()));
  },
  getTrialTemplateId: () => selectTrialTemplateId(forceListingStore.getState()),
  isPricingValid: () => {
    var _listing$id, _listing$external_id, _listing$org62Account;
    const {
      listing
    } = listingStore.getState();
    const {
      model,
      priceModelType,
      commerceProductId
    } = pricingStore.getState();
    const listingId = (_listing$id = listing.id) !== null && _listing$id !== void 0 ? _listing$id : '';
    const listingExternalId = (_listing$external_id = listing.external_id) !== null && _listing$external_id !== void 0 ? _listing$external_id : '';
    const org62AccountId = (_listing$org62Account = listing.org62AccountId) !== null && _listing$org62Account !== void 0 ? _listing$org62Account : '';
    const record = pricingModelToRecord(model, priceModelType, listingId, listingExternalId, org62AccountId, commerceProductId);
    const creditIdentifier = selectCreditorIdentifier(forceListingStore.getState());
    return validatePricingClientSide(record, creditIdentifier).isValid;
  },
  isApproved: () => {
    const {
      approvalStatus
    } = approvalsStore.getState();
    const {
      ISVClickthroughName
    } = selectISVClickthrough(publisherStore.getState());
    const isApproved = approvalStatus === BUSINESS_APPROVAL_STATUSES.Approved;
    const isAgreed = isApproved ? !!ISVClickthroughName : false;
    return pricingStore.getState().paymentType === PaymentTypes.Checkout ? isAgreed : isApproved;
  }
});
export function updateListingStoreValidator() {
  listingStoreValidator.listing = selectListing(listingStore.getState());
  listingStoreValidator.resetCachedStepsCompleted();
}
listingStore.subscribe(updateListingStoreValidator, selectListing);
pricingStore.subscribe(updateListingStoreValidator, ({
  priceModelType,
  model,
  paymentType
}) => ({
  priceModelType,
  model,
  paymentType
}), equalityFn);

// Use this function to get the currently active technology store
export function getTechnologyStore() {
  var _activeTechnologyStor3;
  if (!((_activeTechnologyStor3 = activeTechnologyStore) !== null && _activeTechnologyStor3 !== void 0 && _activeTechnologyStor3.store)) {
    throwDevelopmentOrLog(new Error('Missing active technology store'));
    return undefined;
  }
  return activeTechnologyStore.store;
}
export function selectIsLoading(state) {
  return state.isLoading;
}
export function selectListing(state) {
  return state.listing;
}
export function selectListingType(state) {
  return selectListing(state).technology;
}
export function selectTitle(state) {
  return selectListing(state).title;
}
export function selectCarouselItems(state) {
  return selectCarousel(selectListing(state)).items;
}
export function selectContentItems(state) {
  return selectContent(selectListing(state)).items;
}
export function selectBusinessEmail(state) {
  return selectSupportInfo(selectListing(state)).businessEmail || '';
}
export function selectReviewsEmail(state) {
  return selectSupportInfo(selectListing(state)).reviewsEmail || '';
}
export function selectSupportEmailAddress(state) {
  return selectSupportInfo(selectListing(state)).supportEmailAddress || '';
}
export function selectSupportPhoneNumber(state) {
  return selectSupportInfo(selectListing(state)).supportPhoneNumber || '';
}
export function selectSupportDetails(state) {
  return selectSupportInfo(selectListing(state)).supportDetails || '';
}
export function selectExtensionListing(listing) {
  var _listing$extensions$f2;
  const extensionType = TECHNOLOGY_EXTENSION_MAP[listing.technology];
  return (_listing$extensions$f2 = listing.extensions.find(test => test.extensionType === extensionType)) === null || _listing$extensions$f2 === void 0 ? void 0 : _listing$extensions$f2.data;
}
export function selectSupportInfo(listing, isDraftState) {
  const pluginType = 'listing/plugins/SupportInformation';
  let plugin = listing.plugins.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? listing : undefined);
  }
  return plugin.data;
}
export function selectLogoSet(listing, isDraftState) {
  const pluginType = 'listing/plugins/LogoSet';
  let plugin = listing.plugins.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? listing : undefined);
  }
  return plugin.data;
}
export function selectLogoSetItems(listing) {
  return selectLogoSet(listing).items;
}
export function selectCategories(listing) {
  var _selectExtensionListi, _selectExtensionListi2;
  return (_selectExtensionListi = (_selectExtensionListi2 = selectExtensionListing(listing)) === null || _selectExtensionListi2 === void 0 ? void 0 : _selectExtensionListi2.listingCategories) !== null && _selectExtensionListi !== void 0 ? _selectExtensionListi : [];
}
export function selectLanguages(listing) {
  var _selectExtensionListi3, _selectExtensionListi4;
  return (_selectExtensionListi3 = (_selectExtensionListi4 = selectExtensionListing(listing)) === null || _selectExtensionListi4 === void 0 ? void 0 : _selectExtensionListi4.languages) !== null && _selectExtensionListi3 !== void 0 ? _selectExtensionListi3 : [];
}
export function selectRequiredProducts(listing) {
  var _selectExtensionListi5, _selectExtensionListi6;
  return (_selectExtensionListi5 = (_selectExtensionListi6 = selectExtensionListing(listing)) === null || _selectExtensionListi6 === void 0 ? void 0 : _selectExtensionListi6.productsRequired) !== null && _selectExtensionListi5 !== void 0 ? _selectExtensionListi5 : [];
}
export function selectSupportedProducts(listing) {
  var _selectExtensionListi7, _selectExtensionListi8;
  return (_selectExtensionListi7 = (_selectExtensionListi8 = selectExtensionListing(listing)) === null || _selectExtensionListi8 === void 0 ? void 0 : _selectExtensionListi8.productsSupported) !== null && _selectExtensionListi7 !== void 0 ? _selectExtensionListi7 : [];
}
export function selectSupportedFeatures(listing) {
  const supportedFeatures = [];
  const forceListing = selectExtensionListing(listing);
  if (forceListing !== null && forceListing !== void 0 && forceListing.isSalesforceShield) {
    supportedFeatures.push('isSalesforceShield');
  }
  if (forceListing !== null && forceListing !== void 0 && forceListing.isMultiCurrency) {
    supportedFeatures.push('isMultiCurrency');
  }
  if (forceListing !== null && forceListing !== void 0 && forceListing.isPersonAccounts) {
    supportedFeatures.push('isPersonAccounts');
  }
  if (forceListing !== null && forceListing !== void 0 && forceListing.isLightningReady) {
    supportedFeatures.push('isLightningReady');
  }
  if (forceListing !== null && forceListing !== void 0 && forceListing.isSalesProgram) {
    supportedFeatures.push('isSalesProgram');
  }
  if (forceListing !== null && forceListing !== void 0 && forceListing.isEinsteinAgent) {
    supportedFeatures.push('isEinsteinAgent');
  }
  return supportedFeatures;
}
export function selectCarousel(listing, isDraftState) {
  const pluginType = 'listing/plugins/Carousel';
  let plugin = listing.plugins.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? listing : undefined);
  }
  return plugin.data;
}
export function selectContent(listing, isDraftState) {
  var _plugin;
  const pluginType = 'listing/plugins/Content';
  let plugin = listing.plugins.find(test => test.pluginType === pluginType);
  let content = (_plugin = plugin) === null || _plugin === void 0 ? void 0 : _plugin.data;
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? listing : undefined);
    content = plugin.data;
  } else {
    var _content;
    (_content = content) === null || _content === void 0 ? void 0 : _content.items.forEach(generateKeyForIterable);
  }
  return content;
}
export function selectDemo(listing, isDraftState) {
  const pluginType = 'listing/plugins/Demo';
  let plugin = listing.plugins.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? listing : undefined);
  }
  return plugin.data;
}
export function selectDemoUrl(listing) {
  var _selectDemo$items$0$u, _selectDemo, _selectDemo$items, _selectDemo$items$;
  return (_selectDemo$items$0$u = (_selectDemo = selectDemo(listing)) === null || _selectDemo === void 0 ? void 0 : (_selectDemo$items = _selectDemo.items) === null || _selectDemo$items === void 0 ? void 0 : (_selectDemo$items$ = _selectDemo$items[0]) === null || _selectDemo$items$ === void 0 ? void 0 : _selectDemo$items$.url) !== null && _selectDemo$items$0$u !== void 0 ? _selectDemo$items$0$u : '';
}
export function selectIsSaving(state) {
  return state.isSaving;
}
export function selectLeadTrialInformation(state, isDraftState) {
  var _selectListing$plugin;
  const pluginType = 'listing/plugins/LeadTrialInformation';
  let plugin = (_selectListing$plugin = selectListing(state).plugins) === null || _selectListing$plugin === void 0 ? void 0 : _selectListing$plugin.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? state.listing : undefined);
  }
  return plugin.data;
}
export function selectTrialTemplate(state, isDraftState) {
  var _selectListing$plugin2;
  const pluginType = 'listing/plugins/TrialTemplate';
  let plugin = (_selectListing$plugin2 = selectListing(state).plugins) === null || _selectListing$plugin2 === void 0 ? void 0 : _selectListing$plugin2.find(test => test.pluginType === pluginType);
  if (!plugin) {
    plugin = listingStore.upsertPlugin({
      pluginType,
      data: schemas[pluginType].new()
    }, isDraftState ? state.listing : undefined);
  }
  return plugin.data;
}
export function selectConsultantLeads(state, isDraftState) {
  const leadTrialInformation = selectLeadTrialInformation(state, isDraftState);
  const leads = {
    leadsOrgId: leadTrialInformation.leadsOrgID || '',
    watchADemo: {
      hasMeetPrerequisites: false,
      isChecked: leadTrialInformation.enableDemoLeads || false,
      campaignID: leadTrialInformation.demoCampaignID || ''
    },
    watchLearnMore: {
      hasMeetPrerequisites: false,
      isChecked: leadTrialInformation.enableLearnMoreLeads || false,
      campaignID: leadTrialInformation.learnMoreCampaignID || ''
    }
  };
  return leads;
}
export function selectLeads(state, isDraftState) {
  var _selectTestDrive;
  const leadTrialInformation = selectLeadTrialInformation(state, isDraftState);
  const solutionId = selectSolutionId(forceListingStore.getState());
  const trialTemplateId = selectTrialTemplateId(forceListingStore.getState());
  const enableTestDrive = (_selectTestDrive = selectTestDrive(forceListingStore.getState())) === null || _selectTestDrive === void 0 ? void 0 : _selectTestDrive.enableTestDrive;
  const leads = {
    leadsOrgId: leadTrialInformation.leadsOrgID || '',
    watchADemo: {
      hasMeetPrerequisites: false,
      isChecked: leadTrialInformation.enableDemoLeads || false,
      campaignID: leadTrialInformation.demoCampaignID || ''
    },
    testDrive: {
      hasMeetPrerequisites: enableTestDrive,
      isChecked: leadTrialInformation.enableTestDriveLeads || false,
      campaignID: leadTrialInformation.testDriveCampaignID || ''
    },
    createTrial: {
      hasMeetPrerequisites: Boolean(trialTemplateId),
      isChecked: leadTrialInformation.enableTrialLeads || false,
      campaignID: leadTrialInformation.trialCampaignID || ''
    },
    installApp: {
      hasMeetPrerequisites: Boolean(solutionId),
      isChecked: leadTrialInformation.enableInstallLeads || false,
      campaignID: leadTrialInformation.installCampaignID || ''
    }
  };
  return leads;
}
function sortItems(items) {
  //If there is no position populated use the index of the array to populate it
  if (items.length > 0 && !items[0].data.position) {
    for (let i = 0; i < items.length; i++) {
      items[i].data.position = i;
    }
    return items;
  }
  return items.sort((a, b) => a.data.position - b.data.position);
}
listingStore.subscribe(setRequireResubmission, selectTitle);
function setRequireResubmission() {
  if (listingStore.getState().isLoading) {
    return;
  }
  if (pricingStore.getState().paymentType !== PaymentTypes.Checkout) {
    pricingStore.setRequireResubmission();
  }
}